import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs } from "../../../Styles";
import { NumberDisplayList, UNIT, StationContent, NewButtonList, TenDaysGraphComponentsStation } from "../..";
import { currentInnerContentTypes, useModalContext } from "../../GlobalModals/_GlobalModals/globalModalContext";
import { useStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
import { useStationVisitCountById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitCountSlice";
import { useStationConsumptionById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationConsumptionSlice";
import { fetchStationConsumptionByStationIdsThunk, fetchStationStatusByStationIdsThunk, fetchStationVisitCountByStationIdsThunk } from "../../../Store/Thunks/stationStatusByIds";
import { useAppDispatch } from "../../../Store";
import { ButtonTypes } from "../../../Definitions";
import { getStationStatusConfig } from "../StationLayouts/esfStationCore";
interface stationModal_Props {
  activeStation: number;
}

export default function EsfStationModal({ activeStation }: stationModal_Props) {
  const { t } = useTranslation();
  const stationStatusConfig = getStationStatusConfig(t);
  const dispatch = useAppDispatch();
  const { state: { currentInnerContent }, dispatch: dispatchModal } = useModalContext();
  const { isLoading: isStationStatusLoading, entity: stationStatus } = useStationStatusById(activeStation); 
  const { isLoading: isStationVisitCountLoading, entity: stationCount } = useStationVisitCountById(activeStation); 
  const { isLoading: isStationConsumptionLoading, entity: stationConsumption } = useStationConsumptionById(activeStation); 

  const stationStatusContent = stationStatusConfig[Number(stationStatus?.stationState)]

  useEffect(() => {
    if (!activeStation) return;

    const fetchData = () => {
      dispatch(fetchStationStatusByStationIdsThunk([activeStation]));
      dispatch(fetchStationConsumptionByStationIdsThunk([activeStation]));
      dispatch(fetchStationVisitCountByStationIdsThunk([activeStation]));
    };

    fetchData();

    const interval = setInterval(fetchData, 2000);

    return () => clearInterval(interval);
  }, [activeStation, dispatch]);

  const activeContent = (index: number): boolean => {
    return (
      (currentInnerContent === currentInnerContentTypes.stations && index === 0) ||
      (currentInnerContent === currentInnerContentTypes.feeding && index === 1) ||
      (currentInnerContent === currentInnerContentTypes.logs && index === 2)
    );
  };

  // #region | NumberDisplayList
  const FeedCorrections: JSX.Element = useMemo(() => {
    const fedTodayKg = (stationConsumption?.consumptions.filter(s => s.milestone_day_offset === 0 && s.fk_components !== 1).flatMap(s => s.accumulated_amount).reduce((a, b) => a + b, 0) ?? 0) / 1000
    const usesToday = stationCount?.stationVisitCount.find(s => s.day_offset === 0)?.station_visits
    
    return (
      <NumberDisplayList
        height="fit-content"
        gridRows="1fr"
        units={[
          <UNIT content={t("units.kg", {defaultValue: "kg"})} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>,
          // <UNIT content={usesToday === 1 ? t("units.time", {defaultValue: "time"}) : t("units.times", {defaultValue: "times"})} color={colors.greyPrimary}/>
        ]}
        values={[
          fedTodayKg,
          // usesToday ?? 0
        ]}
        headers={[
          `${t("esfWalkThroughPages.sections.fedToday", { defaultValue: "fed today" })}`,
          // `${t("esfWalkThroughPages.sections.usesToday", { defaultValue: "uses today" })}`
        ]}
      />
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, stationConsumption, stationCount])

  // #region | Content Router
  const content_Router = () => {
    switch (currentInnerContent) {
      case currentInnerContentTypes.feeding:
        return (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          {!activeStation || activeStation === 0 || !stationConsumption ? 
          <p style={{...css.emptyPen, opacity: "0.5"}}>{t("noData", { defaultValue: "no data" })}</p>
          : <TenDaysGraphComponentsStation data={stationConsumption}/>}
        </div>)
      case currentInnerContentTypes.logs:
        return ( <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "10px"}}> 
          <p style={{...css.emptyPen}}>Future feature...</p>
          <p style={{...css.emptyPen}}>(Last 10 events/eartags)</p>
        </div>)
      case currentInnerContentTypes.stations: default:
        return ( <StationContent station={activeStation}/> )
    }
  }
  // #endregion

  return (<>
    <div style={{ ...css.modalTop, height: "fit-content", alignItems: "flex-start" }}>
      <div style={{ display: "grid", gridTemplateColumns: "55px 1fr", gridTemplateRows: "1fr 1fr", columnGap: "10px", rowGap: "2px", backgroundColor: colors.greyLightSecondary, borderRadius: "10px", height: "57px", width: "220px", boxSizing: "border-box", padding: "5px 10px" }}>
        <div style={{gridArea: "1/1/3/2", borderRight: "2px solid "+colors.greyTertiary, paddingRight: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>{stationStatusContent?.glyph ?? <glyphs.Setting size="35px" fillColor={colors.greyPrimary}/>}</div>
        <div style={{ gridArea: "1/2/2/3", display: "flex", alignItems: "flex-end" }}><p style={{...fonts.fontSize3, ...fonts.header, color: colors.greyPrimary}}>{`${t("esfWalkThroughPages.sections.stationsState", {defaultValue: "station state"})}`}</p></div>
        <div style={{ gridArea: "2/2/3/3", display: "flex", alignItems: "flex-start" }}>
          <div style={{display: "flex", alignItems: "center", gap: "5px"}}>
            {stationStatusContent ? <glyphs.Dot color={stationStatusContent.color}/> : undefined}
            <p style={{ ...(stationStatusContent ? undefined : css.emptyPen), opacity: (stationStatusContent ? "1" : "0.5"), ...fonts.fontSize4, ...fonts.paragraph, color: stationStatusContent?.color ?? colors.greyDarkPrimary}}>{stationStatusContent?.label ?? t("noData", { defaultValue: "no data" })}</p>
          </div>
        </div>
      </div>
      {FeedCorrections}
    </div>
    <div style={{ ...css.seperatorLine }} />
    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", gap: "20px" }}>
      <div style={{display: "flex", alignItems: "center", justifyContent: "center", padding: "0px 10px", borderTop: "1px solid "+colors.greyTertiary, borderBottom: "1px solid "+colors.greyTertiary, height: "40px", marginTop: "5px"}}>
        <NewButtonList
          contents={[
            t("esfWalkThroughPages.sections.station", {defaultValue: "station"}),
            t("esfWalkThroughPages.sections.stationFeeding", {defaultValue: "feeding"}),
            // t("esfWalkThroughPages.sections.stationLog", { defaultValue: "log" }),
          ]}
          onClick={(i) => {
            if (i === 0) { dispatchModal({currentInnerContent: currentInnerContentTypes.stations}) }
            if (i === 1) { dispatchModal({currentInnerContent: currentInnerContentTypes.feeding}) }
            // if (i === 2) { dispatchModal({currentInnerContent: currentInnerContentTypes.logs}) }
          }}
            buttonType={ButtonTypes.textButton}
            listOverrides={{
              listGap: "5px"
            }}
          overrides={(index) => ({
            baseStyles: {
              backgroundColor: activeContent(index) ? colors.greyDarkPrimary : colors.transparent,
              padding: "8px 10px",
              borderRadius: "10px",
            },
            hoverStyles: {
              opacity: activeContent(index) ? 1 : 0.5,
            },
            contentStyles: {
              color: activeContent(index) ? colors.greyLightTertiary : colors.greyDarkPrimary,
            }
          })}
        />
      </div>
      {content_Router()}
    </div>
  </>)
}